/**
 * Dialogs, drawers and toasts.
 *
 * Built on the native `<dialog>` element, which gives focus trapping, Escape handling
 * and the top layer for free — all things a hand-rolled modal gets subtly wrong, and
 * all things a Hebrew financial app cannot afford to get wrong on a destructive
 * confirmation.
 *
 * Hairline borders only. `--shadow-float` belongs to the marketing hero; an app dialog
 * separates itself with a rule and a dimmed backdrop.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Button, IconButton } from "./primitives.js";
import type { Tone } from "@onecpa/shared";

export interface DialogProps {
  open: boolean;
  onClose(): void;
  title: string;
  description?: string | undefined;
  children?: ReactNode | undefined;
  footer?: ReactNode | undefined;
  /** Narrow for confirmations, wide for forms. */
  size?: "sm" | "md" | "lg" | undefined;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  const width = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" }[size];

  return (
    <dialog
      ref={ref}
      // `cancel` fires on Escape and on backdrop dismissal; routing both through the
      // caller's onClose keeps React state and the DOM in agreement.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      // A dialog genuinely floats, so it is the one place elevation is earned:
      // hairline plus shadow-overlay. Everything that merely sits on the page
      // still separates with a rule alone.
      className={`ruled m-auto w-[calc(100%-2rem)] ${width} bg-surface p-0 text-ink shadow-overlay backdrop:bg-night/50`}
      aria-labelledby="dialog-title"
    >
      <div className="flex items-start gap-3 border-b border-rule px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 id="dialog-title" className="text-md font-bold text-ink">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-xs text-ink-muted">{description}</p>}
        </div>
        <IconButton icon="plus" label="סגירה" onClick={onClose} className="rotate-45" />
      </div>

      {children && <div className="max-h-[60dvh] overflow-y-auto px-4 py-3.5">{children}</div>}

      {footer && (
        <div className="flex items-center justify-end gap-2 border-t border-rule bg-paper px-4 py-3">
          {footer}
        </div>
      )}
    </dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose(): void;
  onConfirm(): void;
  title: string;
  message: string;
  confirmLabel?: string;
  /** Destructive confirmations get the red variant — the one place red is a control. */
  destructive?: boolean;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "אישור",
  destructive = false,
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">{message}</p>
    </Dialog>
  );
}

/**
 * A side panel for detail that should not lose the list behind it.
 *
 * Used for the client card's quick view and the message preview: both are things you
 * open, read, and dismiss without wanting to navigate away and come back.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose(): void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        onClick={onClose}
        className="flex-1 bg-night/30"
        aria-label="סגירה"
        tabIndex={-1}
      />
      <div className="flex w-full max-w-md flex-col border-s border-rule bg-surface shadow-pop">
        <div className="flex items-center gap-3 border-b border-rule px-4 py-3">
          <h2 className="min-w-0 flex-1 truncate text-md font-bold text-ink">{title}</h2>
          <IconButton icon="plus" label="סגירה" onClick={onClose} className="rotate-45" />
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3.5">{children}</div>
        {footer && <div className="border-t border-rule bg-paper px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export interface ToastMessage {
  id: string;
  text: string;
  tone?: Tone;
}

const TOAST_TONES: Record<Tone, string> = {
  neutral: "border-rule-strong bg-surface text-ink",
  info: "border-info-ink/30 bg-info-bg text-info-ink",
  success: "border-success-ink/30 bg-success-bg text-success-ink",
  warning: "border-warning-ink/30 bg-warning-bg text-warning-ink",
  danger: "border-danger-ink/30 bg-danger-bg text-danger-ink",
  pending: "border-pending-ink/30 bg-pending-bg text-pending-ink",
};

/**
 * Toasts in a polite live region.
 *
 * `aria-live="polite"` rather than `assertive`: a confirmation should not interrupt
 * someone mid-sentence, and every toast here reports something that already succeeded.
 */
export function ToastStack({
  messages,
  onDismiss,
}: {
  messages: ToastMessage[];
  onDismiss(id: string): void;
}) {
  return (
    <div
      className="fixed bottom-16 start-4 z-50 flex flex-col gap-2 lg:bottom-4"
      aria-live="polite"
      aria-atomic="false"
    >
      {messages.map((message) => (
        <div
          key={message.id}
          className={`ruled flex max-w-sm items-start gap-3 border px-3 py-2 text-xs shadow-raised ${
            TOAST_TONES[message.tone ?? "neutral"]
          }`}
        >
          <span className="flex-1">{message.text}</span>
          <button
            type="button"
            onClick={() => onDismiss(message.id)}
            className="text-current opacity-60 hover:opacity-100"
            aria-label="סגירה"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
